import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { useModal } from '../hooks/useModal';

// Hook per rispettare prefers-reduced-motion
const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return prefersReducedMotion;
};

// Hook per rilevare mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

interface AnimatedCardProps {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}

export const AnimatedCard: React.FC<AnimatedCardProps> = ({
  children,
  delay = 0,
  className = ""
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const isMobile = useIsMobile();

  return (
    <motion.div
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={prefersReducedMotion ? { duration: 0 } : {
        duration: 0.6,
        delay: delay,
        ease: [0.25, 0.46, 0.45, 0.94]
      }}
      whileHover={prefersReducedMotion || isMobile ? {} : {
        y: -8,
        transition: { duration: 0.2 }
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const AnimatedMetric: React.FC<AnimatedCardProps> = ({
  children,
  delay = 0,
  className = ""
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const isMobile = useIsMobile();

  return (
    <motion.div
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={prefersReducedMotion ? { duration: 0 } : {
        duration: 0.5,
        delay: delay,
        ease: "easeOut"
      }}
      whileHover={prefersReducedMotion || isMobile ? {} : {
        scale: 1.02,
        transition: { duration: 0.2 }
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const AnimatedSidebar: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const prefersReducedMotion = usePrefersReducedMotion();

  return (
    <motion.div
      className="h-full"
      initial={prefersReducedMotion ? { opacity: 1 } : { x: -100, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={prefersReducedMotion ? { duration: 0 } : {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94]
      }}
    >
      {children}
    </motion.div>
  );
};

export const AnimatedHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const prefersReducedMotion = usePrefersReducedMotion();

  return (
    <motion.div
      initial={prefersReducedMotion ? { opacity: 1 } : { y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={prefersReducedMotion ? { duration: 0 } : {
        duration: 0.6,
        ease: "easeOut"
      }}
    >
      {children}
    </motion.div>
  );
};

export const AnimatedButton: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
}> = ({ children, onClick, className = "", disabled = false, type = "button" }) => {
  const prefersReducedMotion = usePrefersReducedMotion();

  return (
    <motion.button
      whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
      whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
      transition={{ duration: 0.2 }}
      onClick={onClick}
      disabled={disabled}
      className={`touch-manipulation min-h-[44px] ${className}`}
      type={type}
    >
      {children}
    </motion.button>
  );
};

export const AnimatedModal: React.FC<{
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  footer?: React.ReactNode;
}> = ({ children, isOpen, onClose, title, size = 'lg', footer }) => {
  const modalRef = React.useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = React.useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  const { handleKeyDown, handleBackdropClick } = useModal({
    isOpen,
    onClose,
    preventBodyScroll: true,
    closeOnEscape: true,
    closeOnClickOutside: true
  });

  // Detect mobile viewport
  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle keyboard visibility on mobile (iOS/Android)
  React.useEffect(() => {
    if (!isOpen) return;

    const handleViewportResize = () => {
      if (modalRef.current && window.visualViewport) {
        const viewportHeight = window.visualViewport.height;
        const windowHeight = window.innerHeight;

        if (viewportHeight < windowHeight * 0.75) {
          // Keyboard is likely open
          modalRef.current.style.maxHeight = `${viewportHeight - 20}px`;
        } else {
          modalRef.current.style.maxHeight = '';
        }
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportResize);
      return () => window.visualViewport?.removeEventListener('resize', handleViewportResize);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Desktop size classes
  const sizeClasses = {
    sm: 'sm:max-w-[400px]',
    md: 'sm:max-w-[520px]',
    lg: 'sm:max-w-[680px]',
    xl: 'sm:max-w-[900px]',
    full: 'sm:max-w-[calc(100vw-48px)]'
  };

  // Reduced motion variants - simple fade
  const reducedMotionVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  // Animation variants for mobile (slide up) vs desktop (scale)
  const mobileVariants = {
    initial: { opacity: 0, y: '100%' },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: '100%' }
  };

  const desktopVariants = {
    initial: { opacity: 0, scale: 0.95, y: -10 },
    animate: { opacity: 1, scale: 1, y: 0 },
    exit: { opacity: 0, scale: 0.95, y: -10 }
  };

  const variants = prefersReducedMotion
    ? reducedMotionVariants
    : (isMobile ? mobileVariants : desktopVariants);

  return createPortal(
    <div
      className={`fixed inset-0 z-[9999] flex ${isMobile ? 'items-end' : 'items-center justify-center p-4 sm:p-6 md:p-8'}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="absolute inset-0 bg-black/40 backdrop-blur-[4px]"
        onClick={handleBackdropClick}
      />

      {/* Modal Content */}
      <motion.div
        ref={modalRef}
        initial={variants.initial}
        animate={variants.animate}
        exit={variants.exit}
        transition={{
          duration: isMobile ? 0.35 : 0.25,
          ease: isMobile ? [0.32, 0.72, 0, 1] : [0.4, 0, 0.2, 1]
        }}
        className={`
          relative w-full ${sizeClasses[size]}
          bg-white dark:bg-gray-800
          overflow-hidden flex flex-col
          ${isMobile
            ? 'max-w-full max-h-[calc(100vh-24px)] max-h-[calc(100dvh-24px)] min-h-[50vh] rounded-t-[24px] mt-6'
            : 'max-h-[90vh] rounded-[22px]'
          }
        `}
        style={{
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.08)'
        }}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {/* Drag indicator for mobile */}
        {isMobile && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 bg-slate-300 dark:bg-gray-600 rounded-full z-10" />
        )}

        {/* Header - Sticky */}
        {title && (
          <div className={`
            flex-shrink-0 flex items-center justify-between
            px-5 sm:px-6 py-4 sm:py-5
            border-b border-slate-200/80 dark:border-gray-700
            sticky top-0 bg-white dark:bg-gray-800 z-10
            ${isMobile ? 'pt-6' : ''}
          `}>
            <h2
              id="modal-title"
              className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100 truncate pr-2"
              style={{ letterSpacing: '-0.02em' }}
            >
              {title}
            </h2>
            <button
              onClick={onClose}
              className="w-11 h-11 sm:w-9 sm:h-9 min-w-[44px] sm:min-w-[36px] min-h-[44px] sm:min-h-[36px] rounded-xl flex items-center justify-center text-slate-400 dark:text-slate-500 bg-slate-100/80 dark:bg-gray-700 border border-slate-200/80 dark:border-gray-600 hover:bg-slate-200/80 dark:hover:bg-gray-600 hover:text-slate-600 dark:hover:text-slate-300 transition-all active:scale-95"
              aria-label="Chiudi"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Body - Scrollable */}
        <div
          className="flex-1 px-5 sm:px-6 py-4 sm:py-5 overflow-y-auto overflow-x-hidden overscroll-contain"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {children}
        </div>

        {/* Footer - Sticky (optional) */}
        {footer && (
          <div className={`
            flex-shrink-0 flex items-center justify-end gap-3
            px-5 sm:px-6 py-4 sm:py-4
            border-t border-slate-200/80 dark:border-gray-700
            bg-slate-50/95 dark:bg-gray-800/95
            sticky bottom-0 z-10
            ${isMobile ? 'pb-[calc(1rem+env(safe-area-inset-bottom,0px))] flex-wrap' : ''}
          `}>
            {footer}
          </div>
        )}
      </motion.div>
    </div>,
    document.body
  );
};

export const AnimatedList: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = "" }) => {
  const prefersReducedMotion = usePrefersReducedMotion();

  return (
    <motion.div
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.5 }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const AnimatedListItem: React.FC<{
  children: React.ReactNode;
  index: number;
  className?: string;
}> = ({ children, index, className = "" }) => {
  const prefersReducedMotion = usePrefersReducedMotion();

  return (
    <motion.div
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={prefersReducedMotion ? { duration: 0 } : {
        duration: 0.4,
        delay: index * 0.1,
        ease: "easeOut"
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const AnimatedProgress: React.FC<{
  value: number;
  max: number;
  className?: string;
}> = ({ value, max, className = "" }) => {
  const percentage = (value / max) * 100;
  const prefersReducedMotion = usePrefersReducedMotion();

  return (
    <div className={`w-full bg-white/10 dark:bg-gray-700 rounded-full h-2 sm:h-2 ${className}`}>
      <motion.div
        className="h-2 sm:h-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full"
        initial={{ width: prefersReducedMotion ? `${percentage}%` : 0 }}
        animate={{ width: `${percentage}%` }}
        transition={prefersReducedMotion ? { duration: 0 } : {
          duration: 1,
          ease: "easeOut"
        }}
      />
    </div>
  );
};

export const AnimatedCounter: React.FC<{
  value: number;
  className?: string;
}> = ({ value, className = "" }) => {
  const prefersReducedMotion = usePrefersReducedMotion();

  return (
    <motion.span
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.5 }}
      className={className}
    >
      {value}
    </motion.span>
  );
};

// Export hooks for reuse in other components
export { usePrefersReducedMotion, useIsMobile }; 